import React, { useState, useRef, useEffect, useMemo } from "react";
import { FieldError } from "react-hook-form";

interface Manager {
  _id: string;
  nome_guerra?: string;
  nome?: string;
  name?: string;
  chave?: string;
  funcao?: string;
  diretoria?: string;
  extra?: {
    nome_guerra?: string;
    nome?: string;
    funcao?: string;
  };
}

interface ManagerSelectorProps {
  managers: Manager[];
  loading?: boolean;
  error?: FieldError;
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (term: string) => void;
  className?: string;
}

export function ManagerSelector({
  managers,
  loading = false,
  error,
  value,
  onChange,
  onSearch,
  className = "",
}: ManagerSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSearch, setPendingSearch] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const getDisplayName = (manager: Manager) =>
    manager.nome_guerra ||
    manager.nome ||
    manager.name ||
    manager.extra?.nome_guerra ||
    manager.extra?.nome ||
    manager._id;

  const getChave = (manager: Manager) => manager.chave || `F${manager._id}`;

  const selectedManager = useMemo(
    () =>
      value
        ? managers.find((m) => getDisplayName(m) === value)
        : undefined,
    [value, managers]
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        if (!selectedManager) setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedManager]);

  const filtered = useMemo(() => {
    if (onSearch) return managers.slice(0, 5);
    if (!searchTerm.trim()) return managers.slice(0, 5);
    const term = searchTerm.toLowerCase();
    return managers.filter((m) => {
      const fields = [
        m.nome_guerra,
        m.nome,
        m.name,
        m.chave,
        m.diretoria,
        m.extra?.nome_guerra,
        m.extra?.nome,
      ];
      return fields.some((f) => f?.toLowerCase().includes(term));
    }).slice(0, 5);
  }, [managers, searchTerm, onSearch]);

  const handleSelect = (manager: Manager) => {
    const displayName = getDisplayName(manager);
    onChange?.(displayName);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange?.("");
    setSearchTerm("");
    setPendingSearch(false);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  return (
    <div className={className} ref={containerRef}>
      <label className="block text-gray-bb-1 font-medium mb-2">
        Responsável por aprovar a iniciativa
      </label>
      <p className="text-gray-bb-2 text-sm mb-2 uppercase">NOME DO GESTOR</p>

      {/* Input / selected display */}
      <div className="relative">
        {selectedManager && !isOpen ? (
          <div
            className={`w-full border rounded-lg p-3 flex items-center justify-between cursor-pointer ${
              error ? "border-red-500" : "border-gray-bb-4"
            }`}
            onClick={() => {
              setIsOpen(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          >
            <div>
              <span className="font-medium text-gray-bb-1">
                {getDisplayName(selectedManager)}
                <span className="text-gray-bb-2 ml-1">
                  ({getChave(selectedManager)})
                </span>
              </span>
              {selectedManager.diretoria && (
                <span className="text-sm text-gray-bb-2 ml-2">
                  — {selectedManager.diretoria}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="text-gray-bb-2 hover:text-red-500 text-lg ml-2 flex-shrink-0"
            >
              ✕
            </button>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              setIsOpen(true);
              if (onSearch) {
                setPendingSearch(true);
                clearTimeout(debounceRef.current);
                if (!val.trim()) {
                  setPendingSearch(false);
                  return;
                }
                debounceRef.current = setTimeout(() => {
                  setPendingSearch(false);
                  onSearch(val);
                }, 300);
              }
            }}
            onFocus={() => setIsOpen(true)}
            placeholder="Digite o nome ou matrícula do gestor."
            className={`w-full border rounded-lg p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
              error ? "border-red-500" : "border-gray-bb-4"
            }`}
          />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && searchTerm.trim() && (
        <div className="mt-1 border border-gray-bb-4 rounded-lg max-h-72 overflow-y-auto bg-white shadow-lg z-50 relative">
          {pendingSearch || loading ? (
            <div className="p-3 text-gray-bb-2 text-sm">
              Buscando gestores...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-gray-bb-2 text-sm">
              Nenhum gestor encontrado
            </div>
          ) : (
            filtered.map((manager) => (
              <button
                key={manager._id}
                type="button"
                onClick={() => handleSelect(manager)}
                className="w-full text-left px-4 py-3 hover:bg-blue-50 active:bg-blue-100 border-b border-gray-bb-4 last:border-b-0 transition-colors touch-manipulation"
              >
                <div className="font-medium text-gray-bb-1 text-sm sm:text-base">
                  {getDisplayName(manager)}
                  <span className="text-gray-bb-2 ml-1">
                    ({getChave(manager)})
                  </span>
                </div>
                {manager.diretoria && (
                  <div className="text-xs sm:text-sm text-gray-bb-2 mt-0.5">
                    {manager.diretoria}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {error && (
        <p className="text-red-500 text-sm mt-1">{error.message}</p>
      )}
    </div>
  );
}
